import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ICarDetails } from '@impact/data';
import { ImpactOverlayRef, OverlayService } from '@impactdk/ngx-overlay';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import SwiperCore, { Autoplay, EffectFade, Navigation } from 'swiper';
import { AutoplayOptions } from 'swiper/types';

import { carImageFallbackIcon, chevronLeft, chevronRight, fullScreenIcon } from '../../../icons';
import { getSizedBilinfoImage } from '../../core/helpers';
import { MetaService } from '../../meta/meta.service';
import { FeatureDetectionService } from '../../utils/helpers/feature-detection.service';
import { UsedCarGalleryComponent } from './used-car-gallery.component';
import { SwiperComponent } from 'swiper/angular';

SwiperCore.use([Autoplay, EffectFade, Navigation]);

enum LayoutView {
    SMALL = 'Small',
    LARGE = 'Large',
}

@Component({
    selector: 'app-used-car-hero',
    template: `
        <section class="used-car-hero" *ngIf="imagesReady">
            <!-- LARGE layout -->
            <div class="used-car-hero__grid">
                <div class="used-car-hero__image-wrapper main-image">
                    <swiper
                        *ngIf="isBrowser && imagesReady"
                        class="used-car-hero__swiper"
                        [spaceBetween]="0"
                        [loop]="true"
                        [lazy]="{ loadPrevNext: false }"
                        [preloadImages]="false"
                        (slideChange)="onSlideChange($event)"
                        [autoplay]="setAutoPlay"
                        effect="fade"
                        #swiper>
                        <ng-container *ngFor="let item of slides; let i = index;">
                            <ng-template swiperSlide>
                                <img class="used-car-hero__image" [attr.src]="getSizedImage(item, 1200)" (click)="openImageSlider(i)" />
                            </ng-template>
                        </ng-container>
                    </swiper>

                    <div class="swiper-pagination__active-slide" *ngIf="slides.length > 1">{{ activeIndex }} / {{ slides.length }}</div>

                    <button class="used-car-hero__slider-expand-button" (click)="openImageSlider(activeIndex - 1)">${fullScreenIcon}</button>

                    <div class="used-car-hero__slider-bottom" *ngIf="slides.length > 1">
                        <div class="used-car-hero__slider-dots">
                            <div class="used-car-hero__slider-dot" (click)="gotoSlideNumber(i + 1)" *ngFor="let item of slides; let i = index;" [class]="{'active-dot': activeIndex === i + 1}"></div>
                        </div>
                        <div class="used-car-hero__slider-controls">
                            <button class="used-car-hero__slider-pagination-button" (click)="changeSlide(activeIndex - 1)">
                                ${chevronLeft}
                            </button>
                            <button class="used-car-hero__slider-pagination-button" (click)="changeSlide(activeIndex + 1)">
                                ${chevronRight}
                            </button>
                        </div>
                    </div>
                </div>
                <div class="used-car-hero__tiles" *ngIf="slides.length >= 3">
                    <div class="used-car-hero__image-wrapper image-1">
                        <img
                            *ngIf="firstTileImg; else fallbackTileIcon"
                            class="used-car-hero__image"
                            [attr.src]="firstTileImg"
                            appImageLoadFadeIn
                            width="500"
                            height="500"
                            (click)="openImageSlider(1)"
                        />
                    </div>
                    <div class="used-car-hero__image-wrapper image-2">
                        <img
                            *ngIf="secondTileImg; else fallbackTileIcon"
                            class="used-car-hero__image"
                            [attr.src]="secondTileImg"
                            appImageLoadFadeIn
                            width="500"
                            height="500"
                            (click)="openImageSlider(2)"
                        />
                    </div>
                    <div class="used-car-hero__image-wrapper image-3" *ngIf="thirdTileImg">
                        <img
                            *ngIf="thirdTileImg; else fallbackTileIcon"
                            class="used-car-hero__image"
                            [attr.src]="thirdTileImg"
                            appImageLoadFadeIn
                            width="500"
                            height="500"
                            (click)="openImageSlider(3)"
                        />
                        <div class="used-car-hero__image-overlay" *ngIf="car?.pictures?.length && componentLayout === layoutViewEnum.SMALL" (click)="openImageSlider(0)">
                               {{ 'used_car_details.open_gallery_button_text' | translate: { numberOfImages: car?.pictures?.length } }}
                        </div>
                    </div>
                    <div class="used-car-hero__image-wrapper image-4" *ngIf="componentLayout === layoutViewEnum.LARGE && slides.length >= 4">
                        <img
                            *ngIf="fourthTileImg; else fallbackTileIcon"
                            class="used-car-hero__image"
                            [attr.src]="fourthTileImg"
                            appImageLoadFadeIn
                            width="500"
                            height="500"
                        />
                        <div class="used-car-hero__image-overlay" *ngIf="slides.length >= 5" (click)="openImageSlider(0)">
                            {{ 'used_car_details.open_gallery_button_text' | translate: { numberOfImages: car?.pictures?.length } }}
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <ng-template #fallbackTileIcon>
            <i class="used-car-hero__image used-car-hero__tile-image--fallback" [innerHTML]="carImageFallbackIcon | safe: 'html'"></i>
        </ng-template>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsedCarHeroComponent implements OnInit, OnDestroy {
    @ViewChild('swiper', { static: false }) swiper?: SwiperComponent;
    @Input() car?: ICarDetails;

    layoutViewEnum = LayoutView;
    componentLayout: string;
    isBrowser: boolean;

    // Slider
    slides: string[];
    imagesReady = false;
    activeIndex = 1;
    sliderImageSize = 400;

    // Gallery
    mainImg?: string;
    firstTileImg?: string;
    secondTileImg?: string;
    thirdTileImg?: string;
    fourthTileImg?: string;

    overlay: ImpactOverlayRef;
    isOpen = false;
    carImageFallbackIcon = carImageFallbackIcon;

    autoPlayOptions: AutoplayOptions = {
        delay: 10000,
        disableOnInteraction: true
    };
    autoPlay = true;
    setAutoPlay: boolean | AutoplayOptions = false;

    preloadSliderImg?: HTMLLinkElement;
    preloadTileImg?: HTMLLinkElement;

    private readonly unsubscribe = new Subject<void>();

    constructor(
        private cdr: ChangeDetectorRef,
        private overlayService: OverlayService,
        private breakpointObserver: BreakpointObserver,
        private zone: NgZone,
        private featureDetectionService: FeatureDetectionService,
        private readonly metaService: MetaService
    ) {
        this.isBrowser = this.featureDetectionService.isBrowser();
    }

    ngOnInit(): void {
        this.setAutoPlay = this.autoPlay ? this.autoPlayOptions : false;
        this.componentLayout = LayoutView.SMALL;

        if (this.car?.pictures && this.car?.pictures?.length) {
            this.slides = this.car?.pictures;
            this.initSlider();
        }
    }

    initSlider() {
        // Detect layout changes
        const queries$ = this.breakpointObserver.observe([
            '(min-width: 460px)',
            '(min-width: 600px)',
            '(min-width: 768px)',
            '(min-width: 1024px)',
            '(min-width: 1200px)',
            '(min-width: 1400px)',
        ]);

        queries$.pipe(takeUntil(this.unsubscribe)).subscribe((result: any) => {
            if (!result.matches) {
                this.componentLayout = this.layoutViewEnum.SMALL;
                this.sliderImageSize = 400;
                this.setTileImageSizes(400, 400);
                this.preloadSliderImg = this.metaService.setPreloadResource(this.getSizedImage(this.slides[0], this.sliderImageSize), 'image');
            } else {
                if (result.breakpoints['(min-width: 1400px)'] === true) {
                    this.componentLayout = this.layoutViewEnum.LARGE;
                    this.setTileImageSizes(1200, 500);
                } else if (result.breakpoints['(min-width: 1200px)'] === true) {
                    this.componentLayout = this.layoutViewEnum.LARGE;
                    this.setTileImageSizes(960, 500);
                } else if (result.breakpoints['(min-width: 1024px)'] === true) {
                    this.componentLayout = this.layoutViewEnum.SMALL;
                    this.setTileImageSizes(1200, 400);
                    this.sliderImageSize = 1200;
                } else if (result.breakpoints['(min-width: 768px)'] === true) {
                    this.componentLayout = this.layoutViewEnum.SMALL;
                    this.sliderImageSize = 960;
                    this.setTileImageSizes(960, 500);
                    this.preloadSliderImg = this.metaService.setPreloadResource(this.getSizedImage(this.slides[0], this.sliderImageSize), 'image');
                } else if (result.breakpoints['(min-width: 600px)'] === true) {
                    this.componentLayout = this.layoutViewEnum.SMALL;
                    this.sliderImageSize = 690;
                    this.setTileImageSizes(690, 500);
                    this.preloadSliderImg = this.metaService.setPreloadResource(this.getSizedImage(this.slides[0], this.sliderImageSize), 'image');
                } else if (result.breakpoints['(min-width: 460px)'] === true) {
                    this.componentLayout = this.layoutViewEnum.SMALL;
                    this.sliderImageSize = 640;
                    this.setTileImageSizes(640, 500);
                    this.preloadSliderImg = this.metaService.setPreloadResource(this.getSizedImage(this.slides[0], this.sliderImageSize), 'image');
                }
            }

            this.imagesReady = true;
            this.cdr.markForCheck();
        });
    }

    setTileImageSizes(mainSize: number, tileSize: number) {
        this.mainImg = getSizedBilinfoImage(this.slides?.[0], mainSize);
        this.preloadTileImg = this.metaService.setPreloadResource(this.mainImg, 'image');

        if (this.slides?.[1]) {
            this.firstTileImg = getSizedBilinfoImage(this.slides?.[1], tileSize);
        }
        if (this.slides?.[2]) {
            this.secondTileImg = getSizedBilinfoImage(this.slides?.[2], tileSize);
        }
        if (this.slides?.[3]) {
            this.thirdTileImg = getSizedBilinfoImage(this.slides?.[3], tileSize);
        }
        if (this.slides?.[4]) {
            this.fourthTileImg = getSizedBilinfoImage(this.slides?.[4], tileSize);
        }
    }

    gotoSlideNumber(index: number) {
        if (this.swiper) {
            const swiperInstance = this.swiper.swiperRef;
            swiperInstance.slideTo(index);
        }
    }

    changeSlide(index: number) {
        if (this.swiper) {
            const swiperInstance = this.swiper.swiperRef;

            if (index < this.activeIndex) {
                swiperInstance.slidePrev();
            } else if (index > this.activeIndex) {
                swiperInstance.slideNext();
            }
        }
      }

    onSlideChange(swiper: any) {
        // Looks like this event handler runs outside angular zone which
        // means changes won't be detected as usual.
        // I consider this a bug in swiper...
        // The following will force it to be run inside angular zone.
        this.zone.run(() => {
            this.activeIndex = swiper.realIndex + 1;
        });
    }

    openImageSlider(index: number) {
        this.isOpen = true;
        this.overlay = this.overlayService.open(UsedCarGalleryComponent, {
            hasBackdrop: true,
            backdropClass: 'media-slider__overlay-backdrop',
            panelClass: 'used-car-gallery-overlay-panel',
            positionVertical: {
                placement: 'top',
            },
            data: {
                images: this.car?.pictures,
                startIndex: index
            },
        });

        this.overlay
            .afterClose()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(() => {
                this.isOpen = false;
            });
    }

    getSizedImage(url: string, width: number): string {
        return getSizedBilinfoImage(url, width);
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
        this.preloadSliderImg?.remove();
        this.preloadTileImg?.remove();
    }
}
